/**
 * Activity tracker hook — tracks page visits and favourites in localStorage.
 * Ready to migrate to profile service when available.
 */

import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigationStore } from '../store/navigationStore';
import { getPages } from '../services/navigationRegistry';

/**
 * Hook to automatically track page visits.
 * Place once in the app layout (e.g., in the authenticated layout wrapper).
 */
export function useActivityTracker() {
    const location = useLocation();
    const { addRecentPage } = useNavigationStore();

    useEffect(() => {
        const path = location.pathname;
        const page = getPages().find(p => p.path === path);
        if (page) {
            addRecentPage({
                path: page.path,
                label: page.label,
                category: page.category,
            });
        }
    }, [location.pathname, addRecentPage]);
}

/**
 * Hook to manage favourites for the current page.
 */
export function usePageFavourite() {
    const location = useLocation();
    const { favourites, addFavourite, removeFavourite } = useNavigationStore();

    const currentPath = location.pathname;
    const isFavourite = favourites.some(f => f.path === currentPath);
    const currentPage = getPages().find(p => p.path === currentPath);

    const toggleFavourite = useCallback(() => {
        if (!currentPage) return;
        if (isFavourite) {
            removeFavourite(currentPath);
        } else {
            addFavourite({
                path: currentPage.path,
                label: currentPage.label,
                category: currentPage.category,
            });
        }
    }, [currentPage, currentPath, isFavourite, addFavourite, removeFavourite]);

    return { isFavourite, toggleFavourite, currentPage };
}
